/**
 * <InkReveal> — переход для React.
 *
 *   <InkReveal show={open}>{содержимое}</InkReveal>
 *
 * Компонент держит содержимое в разметке, пока идёт уход, и убирает после.
 * Снаружи достаточно одного булева.
 *
 * ── Почему промисы, а не события ──────────────────────────────────────
 * Раньше здесь была подписка на animationend. У неё есть свойство, которое
 * стоило нам отдельного расследования: react-dom решает, вешать ли слушателя,
 * по НАЛИЧИЮ конструктора AnimationEvent в window. В jsdom его нет, поэтому
 * onAnimationEnd не вызывается никогда — при полностью исправном коде.
 * У Web Animations API есть `finished`, и такого класса ошибок он не создаёт.
 *
 * ── Почему замер, а не проценты ───────────────────────────────────────
 * Форма зависит от размера: разрешение поля, выбор механизма (растру нужна
 * ячейка, рваному краю — амплитуда) и ключ кэша. Значит сначала верстаем,
 * потом меряем, потом печём. Один кадр содержимое скрыто — иначе блок
 * вспыхнет целиком и только потом начнёт проявляться.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'

import { applyMask, breathe, clearMask, fade, spread, type Playback } from '../core/play'
import type { Scope } from '../Ink'
import { useInk } from './InkProvider'

export type Phase = 'hidden' | 'in' | 'settled' | 'out'

export interface InkRevealProps {
  show: boolean
  children: ReactNode
  /** Множитель длительности поверх общего. */
  speed?: number
  /**
   * Уровень перехода. Смена экрана звучит материалом, объект внутри
   * экрана — глубиной. См. `Scope`.
   */
  scope?: Scope
  /** Разово задать механизм: `'burn'` для карты, например. */
  mechanism?: string
  /** Разводит соседние элементы одного списка. */
  salt?: number
  /**
   * Анимировать первое появление.
   *
   * Выключается там, где блок уже должен быть на экране к моменту, когда
   * человек его увидит: разливать чернила по всему, что было при загрузке,
   * — это салют, а не переход.
   */
  appear?: boolean
  className?: string
  /** Показ доигран. */
  onSettled?: () => void
  /** Уход доигран и содержимое убрано. Нужно для последовательной смены. */
  onHidden?: () => void
}

export function InkReveal({
  show,
  children,
  speed = 1,
  scope = 'object',
  mechanism,
  salt = 0,
  appear = true,
  className,
  onSettled,
  onHidden,
}: InkRevealProps) {
  const ink = useInk()
  const [phase, setPhase] = useState<Phase>(() => (show ? (appear ? 'in' : 'settled') : 'hidden'))
  const [measuring, setMeasuring] = useState(show && appear)

  const root = useRef<HTMLDivElement>(null)
  const body = useRef<HTMLDivElement>(null)
  const rim = useRef<HTMLSpanElement>(null)
  const halo = useRef<HTMLSpanElement>(null)
  const playing = useRef<Playback[]>([])

  const stop = useCallback(() => {
    playing.current.forEach((p) => p.cancel())
    playing.current = []
  }, [])

  useEffect(() => {
    setPhase((current) => {
      if (show) return current === 'in' || current === 'settled' ? current : 'in'
      return current === 'hidden' ? 'hidden' : 'out'
    })
    if (show) setMeasuring((m) => (m ? m : phase === 'hidden'))
    // Фаза читается, но в зависимости не идёт: нас интересует ТОЛЬКО
    // смена show. Иначе каждый шаг машины состояний перезапускал бы себя.

  }, [show])

  useLayoutEffect(() => {
    if (phase !== 'in' && phase !== 'out') return
    const el = body.current
    if (!el) return

    stop()

    const rect = el.getBoundingClientRect()
    const width = Math.max(1, Math.round(rect.width))
    const height = Math.max(1, Math.round(rect.height))
    const direction = phase === 'in' ? 'in' : 'out'
    const duration = ink.duration(width, height) * speed

    const prepared = ink.prepare({ width, height, salt, mechanism, scope })
    setMeasuring(false)

    const shots: Playback[] = []

    if (!prepared.mechanism) {
      // Ни механизма, ни листа: спокойный режим или системная настройка.
      // Честное затухание, а не обрубленные чернила — половина эффекта
      // хуже его отсутствия.
      clearMask(el)
      shots.push(fade(el, Math.min(220, duration), direction))
    } else if (prepared.mechanism.fieldless) {
      clearMask(el)
      shots.push(breathe(el, duration, direction, Math.max(width, height)))
      if (halo.current) {
        shots.push({
          ...pulse(halo.current, duration, direction),
        })
      }
    } else if (prepared.sheet) {
      const { sheet, offsetY, stretchY, drift } = prepared

      applyMask(el, sheet, 'content', offsetY, stretchY)
      shots.push(spread(el, { frames: sheet.frames, duration, direction, offsetY, drift }))

      if (ink.bleed && rim.current && prepared.mechanism.lag > 0) {
        applyMask(rim.current, sheet, 'rim', offsetY, stretchY)
        shots.push(spread(rim.current, { frames: sheet.frames, duration, direction, offsetY }))
        shots.push(rimFade(rim.current, duration, direction))
      }
    }

    playing.current = shots

    let cancelled = false
    Promise.all(shots.map((s) => s.finished))
      .then(() => {
        if (cancelled) return
        if (direction === 'in') {
          // Маска снимается совсем: держать её значит держать лишний слой
          // на каждом элементе экрана до конца сессии.
          clearMask(el)
          setPhase('settled')
          onSettled?.()
        } else {
          setPhase('hidden')
          onHidden?.()
        }
      })
      .catch(() => {
        // Отмена анимации отклоняет промис. Это не ошибка, а нормальный
        // ход событий: показ вернули на середине ухода.
      })

    return () => {
      cancelled = true
    }

  }, [phase])

  useEffect(() => stop, [stop])

  if (phase === 'hidden') return null

  const classes = ['ink-root', measuring ? 'ink-measuring' : null, className]
    .filter(Boolean)
    .join(' ')

  return (
    <div ref={root} className={classes} data-ink-phase={phase}>
      {phase !== 'settled' ? (
        <>
          <span ref={rim} className="ink-rim" aria-hidden="true" />
          <span ref={halo} className="ink-halo" aria-hidden="true" />
        </>
      ) : null}
      <div ref={body} className="ink-body">
        {children}
      </div>
    </div>
  )
}

/**
 * Подтёк гаснет к концу.
 *
 * Если этого не сделать, к финалу маска подтёка раскрыта полностью,
 * и цветное пятно остаётся лежать под содержимым навсегда.
 */
function rimFade(el: HTMLElement, duration: number, direction: 'in' | 'out'): Playback {
  const anim = el.animate([{ opacity: 0.32 }, { opacity: 0.32, offset: 0.62 }, { opacity: 0 }], {
    duration,
    easing: 'linear',
    fill: 'both',
    direction: direction === 'out' ? 'reverse' : 'normal',
  })
  return {
    finished: anim.finished.then(() => undefined),
    cancel: () => anim.cancel(),
    finish: () => anim.finish(),
  }
}

/** Ореол дыхания: вспыхивает к середине и уходит. */
function pulse(el: HTMLElement, duration: number, direction: 'in' | 'out'): Playback {
  const anim = el.animate(
    [
      // Ореол идёт ПОЗАДИ содержимого по времени, а не впереди: сначала
      // проступает то, ради чего всё, и только потом набирает глубина.
      // Обратный порядок читался как вспышка блока перед появлением.
      { opacity: 0, transform: 'scale(0.86)' },
      { opacity: 0.4, transform: 'scale(1)', offset: 0.7 },
      { opacity: 0, transform: 'scale(1.04)' },
    ],
    {
      duration,
      easing: 'ease-out',
      fill: 'both',
      direction: direction === 'out' ? 'reverse' : 'normal',
    },
  )
  return {
    finished: anim.finished.then(() => undefined),
    cancel: () => anim.cancel(),
    finish: () => anim.finish(),
  }
}
